import { useContext, useEffect, useRef, useState } from "react";

import { Listbox, Transition } from "@headlessui/react";
import PropTypes from "prop-types";

/** ========== */
import { BankingOption } from "common/contexts";
import useOnClickOutside from "common/hooks/useOnClickOutside";

import Dropdown from "components/atoms/Dropdown";
import { FiChevronDown } from "components/atoms/Icon/fi/FiChevronDown";
import { formatNumber } from "components/templates/hk/guide-and-tool/hong-kong-cost-planning-tool/step5/index";

import styles from "./Index.module.scss";

function Component() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [remoteOpening, setRemoteOpening] = useState("All");
  const [amountOfInitialDeposit, setAmountOfInitialDeposit] = useState("All");
  const { data, setDataTable } = useContext(BankingOption);
  const [listCountry] = useState(["All", ...data.map((item) => item.name)]);
  const multiSelectRef = useRef(null);

  /* Handle Event */
  function isSelected(value) {
    if (selectedBanks.find((el) => el === value)) {
      return true;
    }

    return false;
  }

  const generateLabelValueDropdown = (value) => {
    if (value === "All") {
      return "Amount of initial Deposit";
    }

    if (value < 100001) {
      return `≤ US$${formatNumber(value)}`;
    }

    return "> US$100,000";
  };

  const remoteOpeningItem = (item) => {
    if (item === "All") return "All";

    if (item < 100001) return `≤ US$${formatNumber(item)}`;

    return `> US$${formatNumber(item - 1)}`;
  };

  function handleDeselect(value) {
    if (value === "All") {
      setSelectedBanks([]);
    } else {
      const selectedBanksUpdated = selectedBanks.filter((el) => el !== value);

      if (selectedBanksUpdated.filter((el) => el != "All").length === 0) {
        setSelectedBanks([]);
      } else {
        setSelectedBanks(selectedBanksUpdated.filter((el) => el != "All"));
      }
    }

    setIsOpen(true);
  }

  function handleSelect(value) {
    if (isSelected(value)) {
      handleDeselect(value);
    }

    if (!isSelected(value)) {
      if (value === "All") {
        setSelectedBanks(listCountry);
      } else {
        const selectedBanksUpdated = [
          ...selectedBanks,
          listCountry.find((el) => el === value),
        ].sort((a, b) => a.localeCompare(b));

        if (
          JSON.stringify(selectedBanksUpdated) ===
          JSON.stringify(
            listCountry
              .filter((el) => el != "All")
              .sort((a, b) => a.localeCompare(b))
          )
        ) {
          setSelectedBanks(listCountry);
        } else {
          setSelectedBanks(selectedBanksUpdated);
        }
      }
    }

    setIsOpen(true);
  }

  function getResult() {
    let dataFilter = [...data];

    if (remoteOpening === "Yes") {
      dataFilter =
        dataFilter &&
        dataFilter
          .map((country) => {
            return {
              id: country.id,
              name: country.name,
              banks: country.banks.filter(
                (bank) => bank.personal_visit === false
              ),
            };
          })
          .filter((item) => item.banks.length > 0);
    }
    if (selectedBanks.length > 0) {
      dataFilter = dataFilter.filter((bank) =>
        selectedBanks.includes(bank.name)
      );
    }
    if (parseInt(amountOfInitialDeposit, 10) <= 100000) {
      dataFilter = dataFilter.map((item) => {
        return {
          id: item.id,
          name: item.name,
          banks: item.banks.filter(
            (e) =>
              parseInt(e.initial_deposit_usd, 10) <=
              parseInt(amountOfInitialDeposit, 10)
          ),
        };
      });
    } else if (parseInt(amountOfInitialDeposit, 10) > 100000) {
      dataFilter = dataFilter.map((item) => {
        return {
          id: item.id,
          name: item.name,
          banks: item.banks.filter(
            (e) =>
              parseInt(e.initial_deposit_usd, 10) >
              parseInt(amountOfInitialDeposit, 10) - 1
          ),
        };
      });
    }

    return dataFilter;
  }
  /* Effect */
  useOnClickOutside(multiSelectRef, () => {
    setIsOpen(false);
  });
  useEffect(() => {
    setDataTable(getResult());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteOpening, selectedBanks, amountOfInitialDeposit]);

  return (
    <div className="row justify-between">
      {/* Select Remote Opening */}
      <div className="col-12 lg:col-4">
        <Dropdown
          value={remoteOpening}
          labelValue={
            remoteOpening === "All" ? "Remote Opening" : remoteOpening
          }
          onChange={setRemoteOpening}
          activeIndex={"Remote Opening"}
          className="dropdown-default h-full w-full text-sm"
        >
          <div className="shadow-bbc rounded-b-xl bg-white pb-2">
            {["All", "Yes"].map((item) => {
              return (
                <Listbox.Option
                  key={item}
                  value={item}
                  as="div"
                  className="block cursor-pointer"
                >
                  {({ active, selected }) => (
                    <div
                      className={`${
                        active && " rounded-md bg-bbc-brand-100"
                      }   ${
                        selected && "font-semibold"
                      }  block px-4 py-2  text-bbc-brand-800`}
                    >
                      {item}
                    </div>
                  )}
                </Listbox.Option>
              );
            })}
          </div>
        </Dropdown>
      </div>
      {/* Select MultiBank*/}

      <div className="col-12 lg:col-4">
        <div
          ref={multiSelectRef}
          className={`${styles.MultiSelect} shadow-bbc relative my-2 inline-block w-full rounded-xl bg-white py-3 text-left lg:my-0`}
        >
          <Listbox
            as="div"
            className="space-y-1"
            value={selectedBanks}
            onChange={(value) => handleSelect(value)}
            open={isOpen}
          >
            <div className="relative">
              <div id="banks-check-btn" onClick={() => setIsOpen(!isOpen)}>
                <Listbox.Button
                  className="w-full select-none px-4  text-left text-sm "
                  open={isOpen}
                >
                  <span className="truncate font-medium ">
                    {selectedBanks.length < 1
                      ? "Bank Country"
                      : `Bank Country: ${
                          selectedBanks.find((el) => el === "All")
                            ? selectedBanks.length - 1
                            : selectedBanks.length
                        } selected`}
                  </span>
                  <FiChevronDown
                    className={`float-right mt-1 inline-block align-middle text-bbc-brand-500   
                                         ${
                                           isOpen ? "rotate-180" : "rotate-0"
                                         } transition-all duration-200`}
                    size={20}
                  />
                </Listbox.Button>
              </div>
              <Transition
                id="banks-check-dropdown"
                unmount={true}
                show={isOpen}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options
                  static
                  className="shadow-b-bbc absolute z-50 w-full select-none rounded-b-xl bg-white pt-2 pb-2 text-sm"
                >
                  {listCountry.map((person) => {
                    const selected = isSelected(person);

                    return (
                      <Listbox.Option key={person} value={person}>
                        {({ active }) => (
                          <div
                            className={`${
                              selected
                                ? "bg-bbc-brand-100 text-bbc-brand-700"
                                : "text-bbc-brand-800"
                            } relative cursor-pointer select-none py-2 pl-8 pr-4
                              hover:bg-bbc-brand-100 hover:text-bbc-brand-700`}
                          >
                            <span
                              className={`${
                                selected ? "font-semibold " : ""
                              } block truncate`}
                            >
                              {person}
                            </span>
                            {selected && (
                              <span
                                className={`${
                                  active ? "" : "text-bbc-brand-500"
                                } absolute inset-y-0 left-0 flex items-center pl-1.5`}
                              >
                                <svg
                                  className="h-5 w-5"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </span>
                            )}
                          </div>
                        )}
                      </Listbox.Option>
                    );
                  })}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>
      </div>

      {/* Select Remote Opening */}

      <div className="col-12 lg:col-4">
        <Dropdown
          value={amountOfInitialDeposit}
          labelValue={
            amountOfInitialDeposit &&
            generateLabelValueDropdown(amountOfInitialDeposit)
          }
          onChange={setAmountOfInitialDeposit}
          activeIndex={"Amount of initial Deposit"}
          className="dropdown-default h-full w-full text-sm"
        >
          <div className="shadow-bbc rounded-b-xl bg-white pb-2">
            {["All", 10000, 50000, 100000, 100001].map((item) => {
              return (
                <Listbox.Option
                  key={item}
                  value={item}
                  as="div"
                  className="block cursor-pointer"
                >
                  {({ active, selected }) => (
                    <div
                      className={`${active && " rounded-md bg-bbc-brand-100"} ${
                        selected && "font-semibold"
                      } block px-4 py-2 text-bbc-brand-800`}
                    >
                      {remoteOpeningItem(item)}
                    </div>
                  )}
                </Listbox.Option>
              );
            })}
          </div>
        </Dropdown>
      </div>
    </div>
  );
}
export default Component;

Component.propTypes = {
  data: PropTypes.array,
  children: PropTypes.element,
};
Component.defaultProps = {
  data: [],
  children: <></>,
};
